import React, { useEffect, useRef, useState } from 'react';
import { FormSectionElements } from '../../entities/formTemplateDataType';

interface FadeableWrapperProps {
    field: FormSectionElements;
    children: React.ReactNode;
    onFadeComplete?: () => void;
}

/**
 * Wraps any callout/alert with optional fade-out behaviour.
 * When field.FadeOut is true and field.FadeOutSeconds > 0,
 * the content fades out after the configured delay and is then removed from the DOM.
 */
const FadeableWrapper: React.FC<FadeableWrapperProps> = ({ field, children, onFadeComplete }) => {
    const [isFading, setIsFading] = useState(false);
    const [isHidden, setIsHidden] = useState(false);

    const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasStartedFadeRef = useRef(false);
    const onFadeCompleteRef = useRef(onFadeComplete);

    useEffect(() => {
        onFadeCompleteRef.current = onFadeComplete;
    }, [onFadeComplete]);

    const scriptDelay = field.fadeOutTime != null && field.fadeOutTime > 0 ? field.fadeOutTime : null;
    const designDelay = field.FadeOut && field.FadeOutSeconds > 0 ? field.FadeOutSeconds : null;
    const effectiveDelay = scriptDelay ?? designDelay;

    useEffect(() => {
        return () => {
            if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
            fadeTimerRef.current = null;
            hideTimerRef.current = null;
        };
    }, []);

    useEffect(() => {
        const isFadeConfigured = effectiveDelay != null;
        // Treat `InvisibleByDefault` as hidden immediately to prevent initial render flicker.
        const isAlertVisible = field.Visible !== false && field.InvisibleByDefault !== true;
        const hasNotStartedYet = !hasStartedFadeRef.current;
        const isNotAlreadyHidden = !isHidden;

        const shouldCancelFade =
            (!isFadeConfigured || !isAlertVisible) &&
            hasStartedFadeRef.current &&
            !isHidden;

        if (shouldCancelFade) {
            if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
            fadeTimerRef.current = null;
            hideTimerRef.current = null;
            hasStartedFadeRef.current = false;
            setIsFading(false);
            return;
        }

        if (isFadeConfigured && isAlertVisible && hasNotStartedYet && isNotAlreadyHidden) {
            hasStartedFadeRef.current = true;

            const fadeDelay = (effectiveDelay || 0) * 1000;

            fadeTimerRef.current = setTimeout(() => {
                setIsFading(true);
                
                hideTimerRef.current = setTimeout(() => {
                    field.Visible = false;
                    field.fadeOutTime = undefined;
                    setIsHidden(true);
                    onFadeCompleteRef.current?.();
                }, 500);
            }, fadeDelay);
        }
    }, [effectiveDelay, field.Visible, field.InvisibleByDefault, isHidden]);

    if (field.Visible === false || field.InvisibleByDefault === true || isHidden) {
        return null;
    }

    if (effectiveDelay == null) {
        return <>{children}</>;
    }

    return (
        <div
            style={{
                transition: 'opacity 0.5s ease-out',
                opacity: isFading ? 0 : 1,
            }}
        >
            {children}
        </div>
    );
};

export default FadeableWrapper;
